// Art: the drawings a look owns most of all (LOOKS.md Part 1, "Art") — the
// champion's cup today, drawn in a team's own colors. Clean's is here; a look
// that draws its own says so in its folder (looks/<id>/index.ts, `cup`), and
// the filters a look draws its lines through are mounted here from the same
// registry.

import { useLook } from '../data/look';
import type { CupProps } from '../looks/define';
import { LOOKS, lookOf } from '../looks/registry';

/** Clean's cup: a simple SVG so it can wear any team's colors. */
function CleanCup({ primary, secondary, size = 64 }: CupProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden>
      <defs>
        <linearGradient id="fsp-cup-shine" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M18 8h28v14a14 14 0 0 1-28 0z" fill={primary} stroke={secondary} strokeWidth="2.5" />
      <path
        d="M18 12H9a2 2 0 0 0-2 2c0 8 5 13 11 14M46 12h9a2 2 0 0 1 2 2c0 8-5 13-11 14"
        fill="none"
        stroke={secondary}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M28 36h8v8h-8z" fill={secondary} />
      <path d="M20 44h24v5H20z" fill={primary} stroke={secondary} strokeWidth="2" />
      <path d="M16 49h32v7H16z" fill={secondary} />
      <path d="M18 8h28v14a14 14 0 0 1-28 0z" fill="url(#fsp-cup-shine)" />
    </svg>
  );
}

/**
 * The filters every look draws its lines through, mounted once for the page:
 * a look's stylesheet names one by id, and a CSS filter can only reach an SVG
 * filter in the same document. All of them, not only the chosen look's, so the
 * Appearance screen's samples draw each look as it is.
 */
export function LookDefs() {
  return (
    <svg aria-hidden focusable="false" width="0" height="0" className="fsp-look-defs">
      {LOOKS.map((look) => (look.defs ? <look.defs key={look.id} /> : null))}
    </svg>
  );
}

/** The champion's cup in a team's colors, as the look draws it. */
export function Cup(props: CupProps) {
  const Drawn = lookOf(useLook()).cup ?? CleanCup;
  return <Drawn {...props} />;
}

/**
 * The weekly challenge's banner drawing: a football in flight with confetti,
 * the streaks and confetti in the week's two colors (`--fsp-art-a`, `--fsp-art-b`,
 * set by whoever draws it), the ball in its own leather. Every look draws this
 * one for now, through its own line filter.
 */
export function FootballArt({ size = 96 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden className="fsp-art-football">
      <g
        fill="none"
        stroke="var(--fsp-art-a, currentColor)"
        strokeWidth="4"
        strokeLinecap="round"
        opacity="0.8"
      >
        <path d="M5 62h17M9 72h14M4 82h12" />
      </g>
      <circle cx="80" cy="13" r="4" fill="var(--fsp-art-b, currentColor)" />
      <rect
        x="62"
        y="3"
        width="7"
        height="7"
        rx="1.5"
        transform="rotate(24 65.5 6.5)"
        fill="var(--fsp-art-a, currentColor)"
      />
      <circle cx="89" cy="37" r="3" fill="var(--fsp-art-a, currentColor)" />
      <path d="M13 24l4-7 4 7z" fill="var(--fsp-art-b, currentColor)" />
      <circle cx="30" cy="10" r="2.5" fill="var(--fsp-art-a, currentColor)" />
      <g transform="rotate(-35 54 54)">
        <ellipse
          cx="54"
          cy="54"
          rx="33"
          ry="20"
          fill="#8b4a26"
          stroke="#4f2810"
          strokeWidth="2.5"
        />
        <path
          d="M27 47c-2 5-2 9 0 14M81 47c2 5 2 9 0 14"
          stroke="#f7efe6"
          strokeWidth="3"
          fill="none"
        />
        <path d="M41 54h26" stroke="#f7efe6" strokeWidth="3" strokeLinecap="round" />
        <path
          d="M45 49.5v9M50 49.5v9M55 49.5v9M60 49.5v9M65 49.5v9"
          stroke="#f7efe6"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <ellipse cx="46" cy="44" rx="13" ry="4" fill="#ffffff" opacity="0.18" />
      </g>
    </svg>
  );
}
